import React, { useContext, useState } from "react";
import { useEffect } from "react";
import { useSettings } from "./SettingsProvider";
import { useBoatInfo } from "./BoatInfoProvider";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useAuth from "../hooks/useAuth";
import { useShips } from "./ShipsProvider";

const POSITION_SENDING_INTERVAL = 10000;
const DEFAULT_POSITION = [44.408036, 8.923026];

const LocationContext = React.createContext();

export function useLocation() {
  return useContext(LocationContext);
}

export function LocationProvider({ children }) {
  const { setSocketChannels } = useShips();
  const [position, setPosition] = useState(null);
  const { aisStatus, sendDummyPosition } = useSettings();
  const { boatInfo } = useBoatInfo();
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();

  useEffect(() => {
    if (!auth?.user) return;
    const controller = new AbortController();
    const sendPosition = async (position) => {
      try {
        const boatPosition = {
          id: boatInfo.id,
          mmsi: boatInfo.MMSI,
          latitude: position.latitude,
          longitude: position.longitude,
          speedKmph: position.speedKmph,
          speedKnots: position.speedKnots,
        };
        const response = await axiosPrivate.post(
          "/api/v1/positions/position",
          boatPosition,
          { signal: controller.signal }
        );
        if (
          response?.status === 202 &&
          response?.data?.data?.channels != null
        ) {
          const newChannels =
            response.data.data.channels != null
              ? response.data.data.channels
              : [];
          setSocketChannels((prev) => {
            if (newChannels.length !== prev.length) {
              return newChannels;
            }
            for (let index = 0; index < newChannels.length; index++) {
              const channel = newChannels[index];
              if (!prev.includes(channel)) {
                console.log("Updating socket channels");
                return newChannels;
              }
            }
            return prev;
          });
        }
      } catch (err) {
        console.log("Error sending current position", err);
      }
    };
    if (aisStatus !== true) {
      console.log("AIS system disabled, retrieving position via browser...");
      if (sendDummyPosition === true) {
        const _sendDummyPosition = () => {
          let newPosition = {
            latitude:
              boatInfo.latitude != null
                ? boatInfo.latitude
                : DEFAULT_POSITION[0],
            longitude:
              boatInfo.longitude != null
                ? boatInfo.longitude
                : DEFAULT_POSITION[1],
            speedKmph: boatInfo.speed != null ? boatInfo.speed * 3.6 : 0,
            speedKnots: boatInfo.speed != null ? boatInfo.speed * 1.94384 : 0,
          };
          console.log("Sending dummy position", newPosition);
          sendPosition(newPosition);
          setPosition({
            coords: {
              latitude: newPosition.latitude,
              longitude: newPosition.longitude,
            },
            speed: {
              kmph: newPosition.speedKmph,
              knots: newPosition.speedKnots,
            },
          });
        };
        _sendDummyPosition();
        const intervalId = setInterval(
          _sendDummyPosition,
          POSITION_SENDING_INTERVAL
        );
        return () => {
          controller?.abort();
          clearInterval(intervalId);
        };
      } else {
        // Retrieve current position via browser and send it over REST API
        if (!navigator.geolocation) {
          console.log("Unsupported Geolocation");
        } else {
          const _sendPosition = () => {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                console.log("Retrieved position", position);
                if (position != null && position.coords != null) {
                  let newPosition = {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    speedKmph:
                      position.coords.speed != null
                        ? position.coords.speed * 3.6
                        : 0,
                    speedKnots:
                      position.coords.speed != null
                        ? position.coords.speed * 1.94384
                        : 0,
                  };
                  sendPosition(newPosition);
                  setPosition({
                    coords: {
                      latitude: newPosition.latitude,
                      longitude: newPosition.longitude,
                    },
                    speed: {
                      kmph: newPosition.speedKmph,
                      knots: newPosition.speedKnots,
                    },
                  });
                }
              },
              (error) => {
                console.log("Unable to retrieve your location", error);
              }
            );
          };
          _sendPosition();
          const intervalId = setInterval(() => {
            _sendPosition();
          }, POSITION_SENDING_INTERVAL);
          return () => {
            controller?.abort();
            clearInterval(intervalId);
          };
        }
      }
    }
  }, [
    sendDummyPosition,
    aisStatus,
    auth,
    auth?.user,
    boatInfo,
    axiosPrivate,
    setSocketChannels,
  ]);

  const value = {
    DEFAULT_POSITION,
    position,
    coords:
      position != null && position.coords != null
        ? [position.coords.latitude, position.coords.longitude]
        : null,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}
